import { ChevronDown, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { LessonRow } from '../lesson-row';
import type { SectionProgress } from '@/types';

// Tarjeta colapsable de sección con sus lecciones. Extraído de detail.tsx (ENG-LOC-01).
export function SectionCard({
  section,
  nextLessonId,
  isCollapsed,
  onToggle,
  onComplete,
  isCompleting,
  minVideoPercent,
  hasInstructor,
  enrollmentId,
}: {
  section: SectionProgress;
  nextLessonId: number | null;
  isCollapsed: boolean;
  onToggle: () => void;
  onComplete: (lessonId: number) => void;
  isCompleting: boolean;
  minVideoPercent?: number;
  hasInstructor?: boolean;
  enrollmentId: number;
}) {
  const completedCount = section.lessons.filter((l) => l.is_completed).length;
  const totalCount = section.lessons.length;
  const sectionDone = totalCount > 0 && completedCount === totalCount;
  const sectionPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-accent/50"
      >
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-card-foreground">{section.title}</h3>
          <div className="mt-1 flex items-center gap-2">
            {sectionDone && <CheckCircle className="h-3.5 w-3.5 shrink-0 text-green-500" />}
            <span
              className={cn(
                'text-xs',
                sectionDone ? 'text-green-600 font-medium' : 'text-muted-foreground',
              )}
            >
              {completedCount} de {totalCount} lecciones completadas
            </span>
          </div>
          {/* Section progress bar */}
          <div className="mt-1.5 h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-muted">
            <div
              className={cn(
                'h-full rounded-full transition-all',
                sectionDone ? 'bg-green-500' : 'bg-primary',
              )}
              style={{ width: `${sectionPercent}%` }}
            />
          </div>
        </div>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
            isCollapsed && '-rotate-90',
          )}
        />
      </button>

      {!isCollapsed && (
        <ul className="divide-y divide-border border-t border-border">
          {[...section.lessons]
            .sort((a, b) => a.order - b.order)
            .map((lesson) => (
              <LessonRow
                key={lesson.id}
                lesson={lesson}
                isNext={lesson.id === nextLessonId}
                onComplete={() => onComplete(lesson.id)}
                isCompleting={isCompleting}
                minVideoPercent={minVideoPercent}
                hasInstructor={hasInstructor}
                enrollmentId={enrollmentId}
              />
            ))}
        </ul>
      )}
    </div>
  );
}
